import React, { useState, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';
import axios from 'axios';
import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
  Container,
  Grid,
  useMediaQuery,
  useTheme,
  Backdrop,
  CircularProgress,
  Fade,
  Grow,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import {
  CloudUpload,
  Description,
  Error,
  CheckCircle,
  Warning,
  Refresh,
  Download,
  Upload,
  Send,
  TableChart as TableChartIcon,
  Info as InfoIcon,
  ErrorOutline as ErrorOutlineIcon,
  FileOpen as FileOpenIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import Header from '../Header';
import { local_signage, local_url } from '../Const';

// Enhanced theme based on HorecaUpload
const enhancedTheme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#f50057',
    },
    background: {
      default: '#f5f7fa',
    },
  },
  typography: {
    h4: {
      fontWeight: 600,
      fontSize: '1.8rem',
    },
    h6: {
      fontSize: '1rem',
    },
    subtitle1: {
      fontSize: '0.9rem',
    },
    body1: {
      fontSize: '0.85rem',
    },
    body2: {
      fontSize: '0.8rem',
    },
    button: {
      fontWeight: 500,
      fontSize: '0.8rem',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 2px 12px 0 rgba(0,0,0,0.08)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: 'none',
          padding: '6px 16px',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '8px 12px',
          fontSize: '0.8rem',
        },
        head: {
          backgroundColor: '#f0f5ff',
          fontWeight: 'bold',
          fontSize: '0.8rem',
        },
      },
    },
  },
});

// Enhanced styled components
const StyledContainer = styled(Container)(({ theme }) => ({
  height: 'auto',
  minHeight: 'calc(100vh - 16px)',
  backgroundColor: '#f5f7fa',
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
}));

const EnhancedPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 8,
  overflow: 'hidden',
  boxShadow: '0 2px 12px 0 rgba(0,0,0,0.08)',
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
  borderBottom: '1px solid #e0e0e0',
  paddingBottom: theme.spacing(1),
}));

const SectionBox = styled(Box)(({ theme }) => ({
  backgroundColor: '#f8faff',
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
  border: '1px solid #e3f2fd',
}));

const UploadArea = styled(Box)(({ theme }) => ({
  border: '2px dashed #1976d2',
  borderRadius: '8px',
  padding: theme.spacing(2),
  textAlign: 'center',
  backgroundColor: '#fafafa',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#f0f8ff',
    borderColor: '#1565c0',
    transform: 'translateY(-1px)',
  },
  '&.drag-over': {
    backgroundColor: '#e3f2fd',
    borderColor: '#0d47a1',
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(1.5),
    borderWidth: '2px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
    borderRadius: '6px',
  },
}));

const ClassicButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
  border: 0,
  borderRadius: '20px',
  boxShadow: '0 2px 10px 2px rgba(33, 203, 243, .2)',
  color: 'white',
  height: 36,
  padding: '0 20px',
  fontSize: '14px',
  fontWeight: 'bold',
  textTransform: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(45deg, #1976d2 30%, #1e88e5 90%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 15px 2px rgba(33, 203, 243, .3)',
  },
  [theme.breakpoints.down('sm')]: {
    height: 32,
    fontSize: '12px',
    padding: '0 16px',
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #4caf50 30%, #8bc34a 90%)',
  border: 0,
  borderRadius: '20px',
  boxShadow: '0 2px 10px 2px rgba(76, 175, 80, .2)',
  color: 'white',
  height: 36,
  padding: '0 24px',
  fontSize: '14px',
  fontWeight: 'bold',
  textTransform: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(45deg, #388e3c 30%, #689f38 90%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 15px 2px rgba(76, 175, 80, .3)',
  },
  '&:disabled': {
    background: 'linear-gradient(45deg, #bdbdbd 30%, #e0e0e0 90%)',
    boxShadow: 'none',
    transform: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    height: 32,
    fontSize: '12px',
    padding: '0 20px',
  },
}));

const ErrorCell = styled(TableCell)(() => ({
  backgroundColor: '#ffebee',
  color: '#c62828',
  fontWeight: 'bold',
  border: '2px solid #ef5350',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    right: '5px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '6px',
    height: '6px',
    backgroundColor: '#d32f2f',
    borderRadius: '50%',
  }
}));

export const BatchRateUpload = () => {
  // Enhanced state management
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [data, setData] = useState([]);
  const [validatedData, setValidatedData] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadStats, setUploadStats] = useState({ total: 0, valid: 0, invalid: 0 });
  const [invalidRowsList, setInvalidRowsList] = useState([]);
  const [duplicateRows, setDuplicateRows] = useState([]);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [areaData, setAreaData] = useState([]);
  const [loadingAreas, setLoadingAreas] = useState(true);
  const fileInputRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Load area data on component mount
  useEffect(() => {
    const loadAreaData = async () => {
      try {
        const userId = localStorage.getItem('userId');
        const token = localStorage.getItem('AuthorizationToken');
        
        const response = await axios.get(`${local_url}/api/SFArea/${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        setAreaData(response.data);
        setLoadingAreas(false);
      } catch (error) {
        console.error('Error loading area data:', error);
        setNotification({
          open: true,
          message: 'Failed to load area data. Some validations may not work properly.',
          severity: 'warning'
        });
        setLoadingAreas(false);
      }
    };

    loadAreaData();
  }, []);

  // Helper function to get AreaId from AreaCode
  const getAreaIdByCode = (areaCode) => {
    const area = areaData.find(area => 
      area.areaCode.toLowerCase() === areaCode.toLowerCase()
    );
    return area ? area.sfAreaId : null;
  };

  // Helper function to validate AreaCode
  const isValidAreaCode = (areaCode) => {
    return areaData.some(area => 
      area.areaCode.toLowerCase() === areaCode.toLowerCase()
    );
  };

  // API validation function for batch numbers
  const validateBatchNumberAPI = async (batchNumber) => {
    try {
      // Get current date in YYYYMMDD format
      const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const token = localStorage.getItem('AuthorizationToken');
      
      const apiUrl = `${local_signage}/api/v1/fetchbatchrate?BusinessDate=${currentDate}&BranchID=ALL&PricingSechemeId=ALL&CustomerId=ALL&BatchNumber=${batchNumber}`;
      
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Check if response contains error message
      if (response.data && typeof response.data === 'object' && response.data.message) {
        if (response.data.message.includes('Forbidden') || response.data.message.includes('Invalid token')) {
          return { isValid: false, error: 'invalid_token' };
        }
      }
      
      // Check if response array has data (BatchNumber exists)
      if (Array.isArray(response.data)) {
        if (response.data.length > 0) {
          return { isValid: true, data: response.data[0] };
        } else {
          // Empty array means BatchNumber doesn't exist
          return { isValid: false, error: 'batch_not_exist' };
        }
      }
      
      return { isValid: false, error: 'invalid_response' };
    } catch (error) {
      console.error('API validation error:', error);
      
      // Check if it's a 403 Forbidden error or contains invalid token message
      if (error.response) {
        if (error.response.status === 403) {
          return { isValid: false, error: 'invalid_token' };
        }
        
        // Check response data for forbidden/token error messages
        if (error.response.data && typeof error.response.data === 'object') {
          if (error.response.data.message && 
              (error.response.data.message.includes('Forbidden') || 
               error.response.data.message.includes('Invalid token'))) {
            return { isValid: false, error: 'invalid_token' };
          }
        }
        
        // Check if response data is a string containing the error message
        if (typeof error.response.data === 'string' && 
            (error.response.data.includes('Forbidden') || 
             error.response.data.includes('Invalid token'))) {
          return { isValid: false, error: 'invalid_token' };
        }
      }
      
      return { isValid: false, error: 'api_error' };
    }
  };

  // Enhanced validation with detailed error types
  const validateRow = (row, index) => {
    const errors = [];

    // AreaCode validation
    if (!row.areacode || row.areacode === '') {
      errors.push('missing_areacode');
    } else if (!loadingAreas && !isValidAreaCode(row.areacode)) {
      errors.push('invalid_areacode');
    }

    // BatchNumber validation
    if (!row.batchnumber || row.batchnumber === '') {
      errors.push('missing_batchnumber');
    }

    // Price validation
    const price = Number(row.price);
    
    if (row.price === undefined || row.price === null || row.price === '') {
      errors.push('missing_price');
    } else if (price < 0) {
      errors.push('negative_price');
    } else if (isNaN(price)) {
      errors.push('non_numeric_price');
    } else if (typeof row.price === 'string' && row.price.includes(',')) {
      errors.push('comma_in_price');
    } else if (String(row.price).includes('.') && String(row.price).split('.')[1]?.length > 2) {
      errors.push('invalid_decimals');
    } else if (String(row.price).split('.').length > 2) {
      errors.push('double_decimal');
    }

    return errors;
  };

  // Enhanced error message mapping
  const getErrorMessage = (errorType) => {
    const errorMessages = {
      missing_areacode: 'Missing AreaCode',
      invalid_areacode: 'Invalid AreaCode - not found in system',
      missing_batchnumber: 'Missing BatchNumber',
      missing_price: 'Missing Price',
      negative_price: 'Negative price value',
      non_numeric_price: 'Price must be numeric',
      comma_in_price: 'Commas not allowed in price',
      invalid_decimals: 'More than 2 decimal places',
      double_decimal: 'Double decimal points',
      batch_not_found: 'Batch number not found in system',
      batch_not_exist: 'BatchNumber not exist',
      invalid_token: 'Forbidden: Invalid token',
      invalid_response: 'Invalid API response',
      api_error: 'API validation failed',
    };
    return errorMessages[errorType] || errorType;
  };

  // Helper function to get row error message
  const getRowErrorMessage = (index) => {
    const rowNum = index + 1;
    const errors = [];

    const invalidRow = invalidRowsList.find(item => item.rowNumber === rowNum);
    if (invalidRow) {
      invalidRow.errors.forEach(errorType => {
        errors.push(getErrorMessage(errorType));
      });
    }

    if (duplicateRows.includes(rowNum)) {
      errors.push("Duplicate record");
    }

    return errors.join(", ");
  };

  // Helper function for row styling
  const getRowStyle = (index) => {
    const rowNum = index + 1;
    const hasError = invalidRowsList.some(item => item.rowNumber === rowNum) || 
                     duplicateRows.includes(rowNum);

    if (hasError) {
      return {
        backgroundColor: '#ffebee',
        '&:hover': { backgroundColor: '#ffcdd2' }
      };
    }

    return {
      '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
      '&:hover': { backgroundColor: '#f5f5f5' }
    };
  };

  // Enhanced file handling
  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
      setValidatedData(null);
      setInvalidRowsList([]);
      setDuplicateRows([]);
      setNotification({
        open: true,
        message: `File "${selectedFile.name}" selected successfully`,
        severity: 'success'
      });
    }
  };

  // Enhanced import processing
  const handleImport = () => {
    if (!file) {
      setNotification({
        open: true,
        message: 'Please select a file first',
        severity: 'warning'
      });
      return;
    }

    setLoading(true);
    setInvalidRowsList([]);
    setDuplicateRows([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          setNotification({
            open: true,
            message: 'No data found in the Excel file',
            severity: 'error'
          });
          setLoading(false);
          return;
        }

        // Check for required columns
        const headers = Object.keys(jsonData[0]);
        const requiredColumns = ['AreaCode', 'BatchNumber', 'Price'];
        const missingColumns = requiredColumns.filter(col =>
          !headers.some(header => header.toLowerCase() === col.toLowerCase())
        );

        if (missingColumns.length > 0) {
          setNotification({
            open: true,
            message: `Missing required columns: ${missingColumns.join(', ')}`,
            severity: 'error'
          });
          setLoading(false);
          return;
        }

        processExcelData(jsonData);
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        setNotification({
          open: true,
          message: 'Error parsing the Excel file. Please make sure it is a valid Excel file.',
          severity: 'error'
        });
        setLoading(false);
      }
    };

    reader.onerror = () => {
      setLoading(false);
      setNotification({
        open: true,
        message: 'Error reading file',
        severity: 'error'
      });
    };

    reader.readAsArrayBuffer(file);
  };

  // Enhanced data processing with API validation
  const processExcelData = async (jsonData) => {
    const invalidRowsList = [];
    const duplicateRowsList = [];
    const itemCombinations = new Map();

    // Process and validate data
    const processedData = jsonData.map((row, index) => {
      // Normalize column names
      const normalizedRow = {};
      Object.keys(row).forEach(key => {
        const normalizedKey = key.toLowerCase().replace(/\s+/g, '');
        normalizedRow[normalizedKey] = row[key];
      });

      const errors = validateRow(normalizedRow, index);

      if (errors.length > 0) {
        invalidRowsList.push({
          rowNumber: index + 1,
          errors: errors
        });
      }

      // Check for duplicates
      const key = `${normalizedRow.areacode || ''}-${normalizedRow.batchnumber || ''}`;
      if (itemCombinations.has(key)) {
        duplicateRowsList.push(index + 1);
      } else {
        itemCombinations.set(key, index + 1);
      }

      return {
        ...normalizedRow,
        rowIndex: index + 1,
        AreaCode: normalizedRow.areacode || '',
        BatchNumber: normalizedRow.batchnumber || '',
        Price: parseFloat(normalizedRow.price) || 0
      };
    });

    // Set initial data to show processing
    setData(processedData);
    setInvalidRowsList(invalidRowsList);
    setDuplicateRows(duplicateRowsList);

    // Now validate batch numbers via API
    setNotification({
      open: true,
      message: 'Validating batch numbers with API...',
      severity: 'info'
    });

    // Get unique batch numbers to avoid duplicate API calls
    const uniqueBatchNumbers = [...new Set(processedData.map(row => row.BatchNumber).filter(batch => batch))];
    
    let apiValidationErrors = 0;
    const batchValidationResults = new Map();

    for (let i = 0; i < uniqueBatchNumbers.length; i++) {
      const batchNumber = uniqueBatchNumbers[i];
      const validationResult = await validateBatchNumberAPI(batchNumber);
      batchValidationResults.set(batchNumber, validationResult);
      
      if (!validationResult.isValid) {
        apiValidationErrors++;
      }
    }

    // Update invalidRowsList with API validation results
    const updatedInvalidRowsList = [...invalidRowsList];
    
    processedData.forEach((row, index) => {
      if (row.BatchNumber) {
        const validationResult = batchValidationResults.get(row.BatchNumber);
        if (validationResult && !validationResult.isValid) {
          const existingErrorIndex = updatedInvalidRowsList.findIndex(item => item.rowNumber === index + 1);
          if (existingErrorIndex >= 0) {
            updatedInvalidRowsList[existingErrorIndex].errors.push(validationResult.error);
          } else {
            updatedInvalidRowsList.push({
              rowNumber: index + 1,
              errors: [validationResult.error]
            });
          }
        }
      }
    });

    setInvalidRowsList(updatedInvalidRowsList);

    // Calculate final stats
    const totalErrors = [...new Set([...updatedInvalidRowsList.map(item => item.rowNumber), ...duplicateRowsList])];
    const validCount = processedData.length - totalErrors.length;

    setUploadStats({
      total: processedData.length,
      valid: validCount,
      invalid: totalErrors.length
    });

    // Set validated data if there are valid records
    if (validCount > 0) {
      const validatedEntries = processedData.filter((_, index) =>
        !totalErrors.includes(index + 1)
      );
      setValidatedData(validatedEntries);
    } else {
      setValidatedData(null);
    }

    // Show final notification
    if (totalErrors.length > 0) {
      let errorMessage = 'Validation errors found: ';
      
      // Group errors by type
      const errorsByType = {};
      updatedInvalidRowsList.forEach(item => {
        item.errors.forEach(errorType => {
          if (!errorsByType[errorType]) errorsByType[errorType] = [];
          errorsByType[errorType].push(item.rowNumber);
        });
      });

      Object.keys(errorsByType).forEach(errorType => {
        errorMessage += `${getErrorMessage(errorType)} in rows: ${errorsByType[errorType].join(', ')}. `;
      });

      if (duplicateRowsList.length > 0) {
        errorMessage += `Duplicate records in rows: ${duplicateRowsList.join(', ')}.`;
      }

      setNotification({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } else {
      setNotification({
        open: true,
        message: `Successfully validated ${processedData.length} items. All batch numbers are valid. Ready to upload.`,
        severity: 'success'
      });
    }

    setLoading(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && (droppedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || droppedFile.name.endsWith('.xlsx'))) {
      // Create a synthetic event for consistency with handleFileChange
      const syntheticEvent = {
        target: {
          files: [droppedFile]
        }
      };
      handleFileChange(syntheticEvent);
    } else {
      setNotification({
        open: true,
        message: 'Please upload a valid .xlsx file',
        severity: 'warning'
      });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Enhanced clear functionality
  const clearFile = () => {
    setFile(null);
    setFileName('');
    setData([]);
    setValidatedData(null);
    setInvalidRowsList([]);
    setDuplicateRows([]);
    setUploadStats({ total: 0, valid: 0, invalid: 0 });
    setErrors({});

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Notification handler
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const getErrorsForRow = (rowIndex) => {
    return errors[rowIndex] || [];
  };

  const hasError = (rowIndex) => {
    return errors[rowIndex] && errors[rowIndex].length > 0;
  };

  const handleSubmit = async () => {
    if (data.length === 0) {
      alert('Please upload a file first');
      return;
    }

    if (uploadStats.invalid > 0) {
      if (!window.confirm(`There are ${uploadStats.invalid} invalid records. Do you want to upload only the valid records?`)) {
        return;
      }
    }

    setUploading(true);
    try {
      // Get token and userId from localStorage
      const token = localStorage.getItem('AuthorizationToken');
      const userId = localStorage.getItem('userId');
      
      // Filter out invalid records
      const validData = data.filter((row, index) => {
        const rowNum = index + 1;
        const hasError = invalidRowsList.some(item => item.rowNumber === rowNum) || 
                         duplicateRows.includes(rowNum);
        return !hasError;
      });
      
      // Prepare data for API according to the specified format
      const uploadData = {
        BatchRateRevId: "",
        FromDate: new Date().toISOString().slice(0, 10).replace(/-/g, ''),
        Remarks: "Price revision for batch upload",
        CreatedUserId: userId || "USR123",
        details: validData.map(row => ({
          AreaId: getAreaIdByCode(row.AreaCode || row.areacode),
          BatchNumber: row.BatchNumber || row.batchnumber,
          Rate: parseFloat(row.Price || row.price),
          DML: "I"
        }))
      };

      // Call the new API endpoint
      const response = await axios.post(`${local_signage}/api/v1/insertbatchpricerevision`, uploadData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 200) {
        setNotification({
          open: true,
          message: `Successfully uploaded ${validData.length} batch rate records!`,
          severity: 'success'
        });
        // Optionally clear the form after successful upload
        clearFile();
      }
    } catch (error) {
      console.error('Upload error:', error);
      setNotification({
        open: true,
        message: 'Failed to upload data. Please try again.',
        severity: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
    <Header/>
    <ThemeProvider theme={enhancedTheme}>
      <Box sx={{
        width: '100vw',
        height: '90vh',
        backgroundColor: 'background.default',
        overflow: 'auto',
        pt: 1,
        pb: 1
      }}>
        <Container maxWidth="lg" sx={{ height: 'auto', minHeight: 'calc(100vh - 16px)' }}>
          <Grow in={true} timeout={800}>
            <EnhancedPaper elevation={3}>
              <HeaderBox>
                <TableChartIcon sx={{ fontSize: 28, mr: 1.5, color: 'primary.main' }} />
                <Typography variant="h4" component="h1">
                  Batch Rate Upload
                </Typography>
                <Tooltip title="Upload Excel files with AreaCode, BatchNumber, and Price columns">
                  <IconButton sx={{ ml: 1.5 }} size="small">
                    <InfoIcon color="action" />
                  </IconButton>
                </Tooltip>
              </HeaderBox>

              {/* Enhanced Upload Section */}
              <SectionBox>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                  Upload Excel Data
                </Typography>

                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Button
                        variant="contained"
                        component="label"
                        startIcon={<FileOpenIcon />}
                        size="small"
                        sx={{
                          mr: 1.5,
                          background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                          boxShadow: '0 2px 4px 1px rgba(33, 203, 243, .2)',
                          color: 'white',
                          '&:hover': {
                            background: 'linear-gradient(45deg, #1976d2 30%, #21a6f3 90%)',
                          }
                        }}
                      >
                        Choose File
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".xlsx, .xls"
                          hidden
                          onChange={handleFileChange}
                        />
                      </Button>

                      {fileName && (
                        <Fade in={!!fileName}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Chip
                              label={fileName}
                              variant="outlined"
                              color="primary"
                              size="small"
                              onDelete={clearFile}
                              deleteIcon={<DeleteIcon />}
                              sx={{ maxWidth: '200px' }}
                            />
                          </Box>
                        </Fade>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleImport}
                      disabled={!file || loading || loadingAreas}
                      fullWidth
                      size="small"
                      sx={{
                        py: 1,
                        background: file && !loading ? 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)' : '',
                        boxShadow: file && !loading ? '0 2px 4px 1px rgba(76, 175, 80, .2)' : '',
                        '&:hover': {
                          background: file && !loading ? 'linear-gradient(45deg, #43A047 30%, #7CB342 90%)' : ''
                        }
                      }}
                    >
                      {loading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CircularProgress size={20} color="inherit" sx={{ mr: 0.5 }} />
                          Processing...
                        </Box>
                      ) : loadingAreas ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CircularProgress size={20} color="inherit" sx={{ mr: 0.5 }} />
                          Loading Areas...
                        </Box>
                      ) : (
                        <>
                          <CloudUpload sx={{ mr: 0.5 }} /> Validate Data
                        </>
                      )}
                    </Button>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleSubmit}
                      disabled={
                        !validatedData ||
                        uploading ||
                        invalidRowsList.length > 0 ||
                        duplicateRows.length > 0
                      }
                      fullWidth
                      size="small"
                      sx={{
                        py: 1,
                        background: validatedData && !uploading && invalidRowsList.length === 0 && duplicateRows.length === 0 ?
                          'linear-gradient(45deg, #FF5722 30%, #FF9800 90%)' : '',
                        boxShadow: validatedData && !uploading && invalidRowsList.length === 0 && duplicateRows.length === 0 ?
                          '0 2px 4px 1px rgba(255, 87, 34, .2)' : '',
                        '&:hover': {
                          background: validatedData && !uploading && invalidRowsList.length === 0 && duplicateRows.length === 0 ?
                            'linear-gradient(45deg, #E64A19 30%, #F57C00 90%)' : ''
                        }
                      }}
                    >
                      {uploading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CircularProgress size={20} color="inherit" sx={{ mr: 0.5 }} />
                          Uploading...
                        </Box>
                      ) : (
                        <>
                          <Send sx={{ mr: 0.5 }} /> Submit
                        </>
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </SectionBox>

              {/* Error Display */}
              {(invalidRowsList.length > 0 || duplicateRows.length > 0) && (
                <Alert
                  severity="warning"
                  sx={{ mb: 1, fontSize: '0.8rem' }}
                  action={
                    <Tooltip title="Rows with errors are highlighted in red">
                      <IconButton size="small">
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <Typography variant="subtitle2">Validation Errors Found:</Typography>
                  <Box component="ul" sx={{ mt: 0.5, pl: 1.5 }}>
                    {invalidRowsList && invalidRowsList.length > 0 && (
                      <li>
                        Data validation errors:
                        <Box component="ul" sx={{ pl: 1.5 }}>
                          {invalidRowsList.map((item, idx) => (
                            <li key={idx}>
                              Row {item.rowNumber}: {item.errors.map(err => getErrorMessage(err)).join(', ')}
                            </li>
                          ))}
                        </Box>
                      </li>
                    )}
                    {duplicateRows.length > 0 && (
                      <li>
                        Duplicate records in rows: {duplicateRows.join(', ')}
                      </li>
                    )}
                  </Box>
                </Alert>
              )}

              {/* Enhanced Table Section */}
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="h6" sx={{ mb: 1.5, display: 'flex', alignItems: 'center' }}>
                  <TableChartIcon sx={{ mr: 0.5, fontSize: 18 }} /> Data Preview
                </Typography>

                {data.length > 0 ? (
                  <Fade in={data.length > 0} timeout={800}>
                    <TableContainer component={Paper} sx={{
                      mt: 1.5,
                      boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
                      borderRadius: 1.5,
                      maxHeight: 350,
                      overflow: 'auto'
                    }}>
                      <Table aria-label="batch rate data table" stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Row</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Area Code</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Batch Number</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Price</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Status</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {data.map((row, index) => {
                            const rowNum = index + 1;
                            const hasError = invalidRowsList.some(item => item.rowNumber === rowNum) ||
                              duplicateRows.includes(rowNum);
                            const errorMsg = getRowErrorMessage(index);

                            return (
                              <TableRow
                                key={index}
                                sx={getRowStyle(index)}
                              >
                                <TableCell>{rowNum}</TableCell>
                                <TableCell>{row.AreaCode || row.areacode || ''}</TableCell>
                                <TableCell>{row.BatchNumber || row.batchnumber || ''}</TableCell>
                                <TableCell align="right">
                                  {typeof row.Price === 'number'
                                    ? `${row.Price.toFixed(2)}`
                                    : (row.price || '')}
                                </TableCell>
                                <TableCell align="center">
                                  {hasError ? (
                                    <Tooltip title={errorMsg}>
                                      <ErrorOutlineIcon color="error" fontSize="small" />
                                    </Tooltip>
                                  ) : (
                                    <Chip
                                      label="Valid"
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      sx={{ fontSize: '0.65rem', height: '20px' }}
                                    />
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Fade>
                ) : (
                  <Fade in={!data.length} timeout={800}>
                    <Box
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        bgcolor: '#fafafa',
                        borderRadius: 1.5,
                        border: '2px dashed #e0e0e0',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <CloudUpload sx={{ fontSize: 48, color: '#bdbdbd', mb: 1.5 }} />
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 0.5 }}>
                        No Data Available
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Upload an Excel file to view your data here
                      </Typography>
                    </Box>
                  </Fade>
                )}
              </Box>

              {/* Success Message */}
              {validatedData && validatedData.length > 0 && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1.5, border: '1px solid #c8e6c9' }}>
                  <Typography variant="subtitle1" sx={{ color: '#2e7d32', fontWeight: 500, display: 'flex', alignItems: 'center' }}>
                    <InfoIcon sx={{ mr: 0.5, fontSize: 18 }} />
                    Data validated successfully. {validatedData.length} valid records ready to upload.
                    {invalidRowsList.length > 0 || duplicateRows.length > 0 ?
                      ` (${invalidRowsList.length + duplicateRows.length} invalid records will be skipped)` :
                      ''}
                  </Typography>
                </Box>
              )}
            </EnhancedPaper>
          </Grow>

          {/* Notification Snackbar */}
          <Snackbar
            open={notification.open}
            autoHideDuration={6000}
            onClose={handleCloseNotification}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert
              onClose={handleCloseNotification}
              severity={notification.severity}
              variant="filled"
              sx={{ width: '100%' }}
            >
              {notification.message}
            </Alert>
          </Snackbar>
        </Container>
      </Box>

      {/* Upload Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={uploading}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress color="inherit" size={40} />
          <Typography variant="body1" sx={{ mt: 1.5, fontSize: '0.9rem' }}>
            Uploading data...
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.75rem' }}>
            Please wait
          </Typography>
        </Box>
      </Backdrop>
    </ThemeProvider>
    </>
  );
};

export default BatchRateUpload;